import { Event } from '../types/event';

export type TimeBucket = 'next7' | 'next30' | 'later';

export interface EventFilters {
  /** Free-text search over title/description/location. */
  search: string;
  /** Selected AppCategory values, matched against event.category. OR within. */
  categories: string[];
  /** Selected Den/location strings, matched against event.location. OR within. */
  dens: string[];
  /** When true, exclude events with no remaining capacity. */
  hasSpotsOnly: boolean;
}

export const DEFAULT_FILTERS: EventFilters = {
  search: '',
  categories: [],
  dens: [],
  hasSpotsOnly: false,
};

export interface GroupedEvents {
  next7: Event[];
  next30: Event[];
  later: Event[];
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse a YYYY-MM-DD string as a local-midnight Date (avoids UTC offset drift). */
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Returns the first rolling bucket the event qualifies for, measured in whole
 * days from start-of-today: next7 = days 0-6, next30 = days 7-29, later = >=30.
 * Past events return null (defensive — they are filtered upstream).
 */
export function bucketForEvent(eventDateISO: string, now: Date): TimeBucket | null {
  const eventDay = parseLocalDate(eventDateISO);
  const today = startOfLocalDay(now);
  const diffDays = Math.round((eventDay.getTime() - today.getTime()) / MS_PER_DAY);
  if (diffDays < 0) return null;
  if (diffDays < 7) return 'next7';
  if (diffDays < 30) return 'next30';
  return 'later';
}

/** Faceted filter: AND across dimensions, OR within each multi-value dimension. */
export function filterEvents(events: Event[], filters: EventFilters): Event[] {
  const search = filters.search.trim().toLowerCase();
  return events.filter((e) => {
    const matchesSearch =
      search === '' ||
      e.title.toLowerCase().includes(search) ||
      e.description.toLowerCase().includes(search) ||
      e.location.toLowerCase().includes(search);
    const matchesCategory =
      filters.categories.length === 0 || filters.categories.includes(e.category);
    const matchesDen =
      filters.dens.length === 0 || filters.dens.includes(e.location);
    const matchesSpots = !filters.hasSpotsOnly || e.currentAttendees < e.capacity;
    return matchesSearch && matchesCategory && matchesDen && matchesSpots;
  });
}

/** Group events into rolling buckets, chronological (date, then startTime) within each. */
export function groupEventsByBucket(events: Event[], now: Date): GroupedEvents {
  const groups: GroupedEvents = { next7: [], next30: [], later: [] };
  const sorted = [...events].sort((a, b) => {
    const byDate = a.date.localeCompare(b.date);
    return byDate !== 0 ? byDate : a.startTime.localeCompare(b.startTime);
  });
  for (const e of sorted) {
    const bucket = bucketForEvent(e.date, now);
    if (bucket) groups[bucket].push(e);
  }
  return groups;
}

export function isAnyFilterActive(filters: EventFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.categories.length > 0 ||
    filters.dens.length > 0 ||
    filters.hasSpotsOnly
  );
}

/**
 * Distinct, alphabetically-sorted, non-empty `event.location` values from the
 * given events. Built from the SAME (already-visible) event set the list is
 * rendered from, so locations whose only events have ended — and are therefore
 * absent from the input — never appear as Location filter options. Raw values
 * are preserved (only blank/whitespace-only are dropped) so they still match
 * `filterEvents`' `filters.dens.includes(e.location)`.
 */
export function getDistinctLocations(events: Event[]): string[] {
  const seen = new Set<string>();
  for (const e of events) {
    if (e.location && e.location.trim() !== '') seen.add(e.location);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}
